import toLower from 'lodash/toLower'
import SockJS from 'sockjs-client'
import { Route } from 'vue-router'
import micromatch from 'micromatch'
import { logger } from '@ustra/core'
import { InterceptorScope } from '@ustra/core/src/utils/axios-utils'
import * as webUtils from '@ustra/core/src/utils/web-utils'
import * as cryptoUtils from '@ustra/core/src/utils/client/crypto-utils'
import * as jwtUtils from '@ustra/core/src/utils/jwt-utils'

import { addModules, callback } from '../../store'
import { AuthModule } from '../../store/modules/auth-module'
import authNuxtMiddleware from '../../middlewares/auth-nuxt-middleware'
import authEventBus from '../../event-bus/global/auth-event-bus'
import * as nuxtUtils from '../../../utils/nuxt-utils'
import { ModuleNameKeys } from '../modules/keys'
import { UstraPlugin } from '../ustra-plugin'
import { HttpMethod } from '@ustra/core/src/server/http/const'

export class Auth {
  private $ustra: UstraPlugin
  private ws: WebSocket = null
  private middleware: any = null
  private moduleMap: any = null

  constructor($ustra: UstraPlugin, middleware: any, moduleMap: any) {
    this.$ustra = $ustra
    this.middleware = middleware
    this.moduleMap = moduleMap
    this.init()
  }

  /**
   * auth module store 조회
   */
  get store() {
    return this.$ustra.store.auth() as AuthModule
  }

  init() {
    addModules(this.$ustra.context, { auth: AuthModule })

    if (this.middleware) {
      this.middleware[authNuxtMiddleware.name] = authNuxtMiddleware.middleware
    }

    if (!this.$ustra.context.isClient) {
      callback.registerBeforeServerInit((moduleName: string) => {
        if (moduleName === 'auth') {
          const _context = nuxtUtils.contextHolder.get()
          const appProp = this.$ustra.env.appProp

          if (appProp.app.auth.jwt.useCookie) {
            // @ts-ignore
            const accessToken = _context.$cookie.get(appProp.app.auth.jwt.accessTokenKey)
            // @ts-ignore
            const refreshToken = _context.$cookie.get(appProp.app.auth.jwt.refreshTokenKey)

            this.serverTokens.setAccessToken(accessToken)
            this.serverTokens.setRefreshToken(refreshToken)
          } else {
            // 쿠키를 사용하지 않을 경우는 서버 상에 토큰을 전달하지 않는다.
            this.serverTokens.setAccessToken('')
            this.serverTokens.setRefreshToken('')
          }
        }
      })
    }

    this.$ustra.api.addRequestInterceptor({
      key: 'auth-req-interceptor',
      scope: InterceptorScope.BOTH,
      onFulfilled: req => {
        const _context = nuxtUtils.contextHolder.get()
        const appProp = this.$ustra.env.appProp

        // 인증 정보가 이미 세팅되어 있을 경우는 SKIP
        if (req.headers[appProp.app.auth.jwt.accessTokenKey] || req.headers[appProp.app.auth.jwt.refreshTokenKey]) {
          return req
        }

        let accessToken = null
        let refreshToken = null

        if (webUtils.isBrowser()) {
          accessToken = _context.$ustra.store.auth().accessToken
          refreshToken = _context.$ustra.store.auth().refreshToken
        } else {
          accessToken = this.serverTokens.getAccessToken()
          refreshToken = this.serverTokens.getRefreshToken()
        }

        if (accessToken) {
          req.headers[appProp.app.auth.jwt.accessTokenKey] = accessToken
        }

        if (refreshToken) {
          req.headers[appProp.app.auth.jwt.refreshTokenKey] = refreshToken
        }
      },
    })

    this.$ustra.api.addResponseInterceptor({
      key: 'auth-res-interceptor',
      scope: InterceptorScope.BOTH,
      onFulfilled: async res => {
        const _context = nuxtUtils.contextHolder.get()
        const appProp = _context.$ustra.env.appProp

        if (this.isExcludeUrlPattern(res?.config?.url)) {
          // logger().warn('is exclude auth pattern', res?.config?.url)
          return res
        }

        // 권한 검증 제외 시
        // @ts-ignore
        if (res.config && res.config.excludeAuthValidation) {
          return res
        }

        if (res.headers) {
          const isAuthenticated = _context.$ustra.store.auth().authenticated
          const accessToken = res.headers[toLower(appProp.app.auth.jwt.accessTokenKey)] || null
          const refreshToken = res.headers[toLower(appProp.app.auth.jwt.refreshTokenKey)] || null
          if (webUtils.isBrowser()) {
            await _context.$ustra.store.auth().reloadToken({
              context: _context,
              accessToken,
              refreshToken,
            })
          } else {
            // 서버 상에 반영
            _context.$ustra.auth.serverTokens.setAccessToken(accessToken)
            _context.$ustra.auth.serverTokens.setRefreshToken(refreshToken)

            await _context.$ustra.store.auth().reloadToken({
              context: _context,
              accessToken,
              refreshToken,
            })
          }

          // 인증 상태였으나, 서버 정책에 의해 만료 되었을 경우 처리
          if (isAuthenticated) {
            const claim = jwtUtils.getClaim(accessToken)
            if (!claim || !claim.sub) {
              await _context.$ustra.auth.doFailureAuth(_context.route, false, 'released', res?.config?.url)
            }
          }
        }
        return res
      },
      onRejected: async error => {
        const _context = nuxtUtils.contextHolder.get()
        _context.$logger().warn('auth plugin ajax error', error?.response)

        if (_context.$ustra.auth.isExcludeUrlPattern(error?.response?.config?.url)) {
          logger().warn('is exclude auth pattern', error?.response?.config?.url)
          throw error
        }

        // 토큰 인증 reject 처리
        if (_context.$ustra.auth.isAuthenticationError(error)) {
          const appProp = _context.$ustra.env.appProp
          if (appProp.app.auth.enabled) {
            await _context.$ustra.auth.doFailureAuth(
              _context.route,
              false,
              error?.response?.status === 409 ? 'duplicated' : 'expired',
              error?.response?.config?.url,
            )
            return null
          }
        }
        throw error
      },
    })
  }

  /**
   * 로그아웃 처리
   */
  logout = async (redirect = true, removeToken = true) => {
    // 이벤트 발생
    authEventBus.emit({ logout: true })

    if (removeToken) {
      await this.store.setAuthInfo(null)
      await this.store.reloadToken({
        context: this.$ustra.context,
        accessToken: null,
        refreshToken: null,
      })
    }

    if (redirect) {
      this.goLoginPage(false)
    }

    if (this.ws) {
      this.ws.close()
    }
  }

  /**
   * 토큰 정보 조회
   */
  getToken = () => {
    return this.store.token
  }

  /**
   * 토큰 클레임 조회
   */
  getTokenClaim = () => {
    return this.store.tokenClaim
  }

  /**
   * 로그인 페이지로 이동
   */
  goLoginPage = (isStore = false) => {
    const redirectUrl = this.$ustra.env.appProp.app.auth.loginUrl

    if (redirectUrl && redirectUrl !== this.$ustra.context.route.path) {
      const redirectOption = { type: 'redirect', url: redirectUrl, query: { rtnUrl: this.$ustra.context.route.fullPath } }
      this.$ustra.context.redirect(redirectOption.url, redirectOption.query)

      // store에서는 redirect가 안되므로 에러 발생
      if (isStore) {
        this.$ustra.nuxt.onReady(() => this.$ustra.context.redirect(redirectOption.url, redirectOption.query))
        // throw new RedirectError({ path: redirectOption.url, query: redirectOption.query })
      }
    }
  }

  /**
   * 인증 실패 처리를 수행한다.
   * @param route 현재 route 정보
   * @param isStore store에서 인증 실패가 되었는지 여부
   * @param failureType 인증 실패 유형
   * @param source 호출 부가 정보(ex:만료URL 등의 정보)
   */
  doFailureAuth = async (
    route: Route = null,
    isStore = false,
    failureType: 'check' | 'expired' | 'duplicated' | 'released' = 'check',
    source: string = null,
  ) => {
    this.$ustra.context.$logger().info('doFailureAuth', failureType, source)

    const authConfig = this.$ustra.env.appProp.app.auth

    if (failureType === 'check') {
      // 이벤트 발생
      authEventBus.emit({ failure: true, route: route || this.$ustra.context.route, context: this.$ustra.context, failureType, apiUrl: source })

      // redirect to login
      if (authConfig.failProcessType === 'redirect') {
        return this.goLoginPage(isStore)
      } else if (authConfig.failProcessType === 'error') {
        this.$ustra.context.error({ statusCode: authConfig.errorStatusCode || 401, path: authConfig.errorUrl })
      }
    } else {
      // 이벤트 발생
      if (authConfig.expiredProcessType === 'event') {
        await authEventBus.emitPromise({
          failure: true,
          route: route || this.$ustra.context.route,
          context: this.$ustra.context,
          failureType,
          apiUrl: source,
        })
      } else {
        authEventBus.emit({ failure: true, route: route || this.$ustra.context.route, context: this.$ustra.context, failureType, apiUrl: source })
      }

      // redirect to login
      // eslint-disable-next-line no-lonely-if
      if (authConfig.expiredProcessType === 'logout') {
        await this.logout()
      } else if (authConfig.expiredProcessType === 'error') {
        this.$ustra.context.error({ statusCode: authConfig.errorStatusCode || 401, path: authConfig.errorUrl })
      }
    }
  }

  /**
   * 인증 여부 확인
   */
  isAuthenticated = () => {
    return this.$ustra.store.auth().authenticated
  }

  /**
   * 인증 관련 오류인지 확인
   */
  isAuthenticationError = (error: Error) => {
    // @ts-ignore
    return error && error.response && (error.response.status === 401 || error.response.status === 409)
  }

  /**
   * 현재 URL이 제외 패턴인지 여부 리턴
   * @param url
   */
  isExcludeUrlPattern = (url: string) => {
    if (!url) {
      return true
    }

    const authProp = this.$ustra.env.appProp.app.auth

    if (!authProp.excludeUrlPatterns) {
      return false
    }

    // console.log('isExcludeUrlPattern', authProp.excludeUrlPatterns, url)

    return micromatch.isMatch(url, authProp.excludeUrlPatterns)
  }

  /**
   * 인증 지속 연결 처리
   * @param userKey 사용자 key 값
   * @param refreshToken refresh token
   * @param procId 프로세스 아이디
   */
  consumeAuthenticationProvider = (userKey: string, refreshToken: string, procId: string) => {
    if (!userKey) {
      return
    }

    const authProp = this.$ustra.env.appProp.app.auth
    const url = authProp.jwt?.duplicationCheckUrl || authProp.jwt?.webSocketUrl

    if (!url) {
      if (authProp.jwt?.duplicationCheckType !== 'none') {
        this.$ustra.logger().warn('duplicationCheckUrl is empty.')
      }

      return
    }

    if (authProp.jwt?.duplicationCheckType === 'websocket') {
      this.checkByWebSocket(userKey, refreshToken, procId, url)
    } else if (authProp.jwt?.duplicationCheckType === 'polling') {
      this.checkByPolling(url)
    }
  }

  /**
   * websocket 중복 체크
   */
  private checkByWebSocket(userKey: string, refreshToken: string, procId: string, url: string) {
    const authProp = this.$ustra.env.appProp.app.auth

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      // token update
      if (this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(
            cryptoUtils.encryptAes256(
              JSON.stringify({ commandType: 'REFRESH_TOKEN_UPDATED', UserKey: userKey, RefreshToken: refreshToken, ProcId: procId }),
              process.env.SERVER_PROP_ENC_KEY,
            ),
          )
        } catch (e) {
          logger().error(e)
        }
      }
      return
    }

    try {
      const param = this.$ustra.api.createUrlEncodedParameter({
        k: cryptoUtils.encryptAes256(
          JSON.stringify({ UserKey: userKey, RefreshToken: refreshToken, ProcId: procId }),
          process.env.SERVER_PROP_ENC_KEY,
        ),
      })
      const sendUrl = url + '?' + param

      try {
        this.ws && this.ws.close()
      } catch (e) {}

      this.ws = new SockJS(sendUrl, null, { timeout: 10000, sessionId: () => procId })

      this.ws.onmessage = async e => {
        try {
          const data = JSON.parse(cryptoUtils.decryptAes256(e.data, process.env.SERVER_PROP_ENC_KEY))

          // 중복 인증 통지
          if (data.commandType === 'DUPLICATED_AUTHENTICATION') {
            await this.doFailureAuth(this.$ustra.context.route, false, 'duplicated', url)
          }

          // 인증 만료
          else if (data.commandType === 'EXPIRED_AUTHENTICATION') {
            await this.doFailureAuth(this.$ustra.context.route, false, 'expired', url)
          }
        } catch (e) {
          logger().error(e)
        }
      }
    } catch (e) {}
  }

  private pollingExecuted: boolean = false

  /**
   * polling 중복 체크
   */
  private checkByPolling(url: string) {
    const authProp = this.$ustra.env.appProp.app.auth
    let timer = null

    if (this.pollingExecuted) {
      return
    }

    this.pollingExecuted = true

    // 인증 처리에서 제거
    if (!authProp.excludeUrlPatterns.some(p => p.startsWith(url))) {
      authProp.excludeUrlPatterns.push('**' + url + '**')
    }

    const doPolling = () => {
      if (!this.isAuthenticated()) {
        if (timer) {
          clearTimeout(timer)
        }
        this.pollingExecuted = false
        return
      }

      const param = this.$ustra.api.createUrlEncodedParameter({
        k: cryptoUtils.encryptAes256(
          JSON.stringify({
            UserKey: this.getTokenClaim().sub,
            RefreshToken: this.getToken()?.refreshToken,
            ProcId: this.getTokenClaim().procId,
          }),
          process.env.SERVER_PROP_ENC_KEY,
        ),
      })
      const sendUrl = url + '?' + param

      this.$ustra.api
        .call({
          url: sendUrl,
          method: HttpMethod.GET,
          showLoading: false,
          passOnResponseError: true,
        })
        .then(async res => {
          // 중복 인증
          if (res?.data?.resultCode === 'FS01') {
            await this.doFailureAuth(this.$ustra.context.route, false, 'duplicated', url)
          }

          // 인증 만료
          if (res?.data?.resultCode === 'FS10') {
            await this.doFailureAuth(this.$ustra.context.route, false, 'expired', url)
          }

          timer = setTimeout(() => doPolling(), authProp.jwt.pollingDurationMilliSec || 10000)
        })
        .catch(e => (timer = setTimeout(() => doPolling(), authProp.jwt.pollingDurationMilliSec || 10000)))
    }

    doPolling()
  }

  /**
   * check module function 조회
   */
  getCheckModuleFunction() {
    return this.moduleMap[ModuleNameKeys.AUTH_CHECK_MODULE]?.default || this.moduleMap[ModuleNameKeys.AUTH_CHECK_MODULE]
  }

  /**
   * 서버 상의 토큰 저장소
   */
  serverTokens = {
    getAccessToken: () => nuxtUtils.namedContextHolder.get('accessToken'),
    setAccessToken: (token: string) => nuxtUtils.namedContextHolder.set('accessToken', token),
    getRefreshToken: () => nuxtUtils.namedContextHolder.get('refreshToken'),
    setRefreshToken: (token: string) => nuxtUtils.namedContextHolder.set('refreshToken', token),
  }
}
